import './customer-account-refresh.css';
import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { request } from '../lib/api';
import { CustomerEmailChange } from '../components/CustomerEmailChange';
import { SecurityCredentials } from '../components/SecurityCredentials';
import { SecurityMethodIcon } from '../components/SecurityMethodIcon';
import { CustomerNavigationIcon } from '../components/CustomerNavigationIcon';
import { useSession } from '../session';
import { useSignOut } from '../useSignOut';
import { formatPlan } from './portalTypes';
import { useDashboardSummary } from './useDashboardSummary';

const planSummary = (plan: string | null | undefined) => {
  switch (plan?.toLowerCase()) {
    case 'boost':
      return 'Easier sharing, a public alias, richer analytics, and proof downloads.';
    case 'secure':
      return 'Private-page access controls with downloadable proofs.';
    case 'premium':
      return 'Marketing, privacy, advanced analytics, and priority support.';
    default:
      return 'Publish your tap page, design a card, and see basic activity.';
  }
};

type BrowserSession = {
  id: string;
  device: string;
  issuedAt: string;
  expiresAt: string;
  lastSeenAt: string;
  rememberMe: boolean;
  current: boolean;
};

const formatSessionTime = (value: string) =>
  new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short'
  }).format(new Date(value));

function SettingsPage() {
  const { session, setSession, replaceSession } = useSession();
  const { summary, loading, error, reload } = useDashboardSummary();
  const signOut = useSignOut();
  const navigate = useNavigate();
  const location = useLocation();
  const [sectionHash, setSectionHash] = useState(() => typeof window === 'undefined' ? '' : window.location.hash);
  useEffect(() => {
    const updateSection = () => setSectionHash(window.location.hash);
    window.addEventListener('hashchange', updateSection);
    return () => window.removeEventListener('hashchange', updateSection);
  }, []);
  const [browserSessions, setBrowserSessions] = useState<BrowserSession[]>([]);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [sessionsReadFailed, setSessionsReadFailed] = useState(false);
  const [sessionsReadAttempt, setSessionsReadAttempt] = useState(0);
  const [sessionError, setSessionError] = useState<string | null>(null);
  const [pendingSessionId, setPendingSessionId] = useState<string | null>(null);
  const plan = summary?.plan.plan ?? session?.subscriptionPlan ?? null;

  useEffect(() => {
    let active = true;
    setSessionsLoading(true);
    setSessionsReadFailed(false);
    setSessionError(null);

    request<{ sessions: BrowserSession[] }>('/auth/sessions')
      .then((response) => {
        if (active) {
          setBrowserSessions(response.sessions);
        }
      })
      .catch((requestError: { data?: { message?: string } }) => {
        if (active) {
          setSessionsReadFailed(true);
          setSessionError(requestError?.data?.message ?? 'Unable to load signed-in devices.');
        }
      })
      .finally(() => {
        if (active) {
          setSessionsLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [sessionsReadAttempt]);

  const revokeSession = async (browserSession: BrowserSession) => {
    if (browserSession.current) {
      signOut();
      return;
    }

    setPendingSessionId(browserSession.id);
    setSessionError(null);
    try {
      await request(`/auth/sessions/${encodeURIComponent(browserSession.id)}/revoke`, { method: 'POST' });
      setBrowserSessions((current) => current.filter(({ id }) => id !== browserSession.id));
    } catch (requestError: any) {
      setSessionError(requestError?.data?.message ?? 'Unable to sign out that device.');
    } finally {
      setPendingSessionId(null);
    }
  };

  const revokeAllSessions = async () => {
    if (!window.confirm('Sign out of InkTap on every device, including this one?')) {
      return;
    }

    setPendingSessionId('all');
    setSessionError(null);
    try {
      await request('/auth/sessions/revoke-all', { method: 'POST' });
      setSession(null);
      navigate('/login', { replace: true });
    } catch (requestError: any) {
      setSessionError(requestError?.data?.message ?? 'Unable to sign out all devices.');
      setPendingSessionId(null);
    }
  };

  return (
    <div className="dashboard-page portal-page portal-workspace settings-overview">
      <header className="portal-intro">
        <h1 tabIndex={-1}>Account settings</h1>
        <p>Manage how you sign in and review your account.</p>
      </header>

      {error ? <div className="overview-recovery"><div className="alert" role="alert">{error}</div><button type="button" className="button-secondary" onClick={(event) => { event.currentTarget.closest('.portal-workspace')?.querySelector('h1')?.focus(); void reload(); }}>Retry account summary</button></div> : null}

      {loading ? (
        <section className="dashboard-section" aria-live="polite">
          <p className="helper-text">Loading your account...</p>
        </section>
      ) : (
        <div className="settings-layout">
          <nav className="settings-section-nav" aria-label="Account settings sections">
            <a href="#settings-email" aria-current={sectionHash === '#settings-email' ? 'location' : undefined}><SecurityMethodIcon method="email" /><span>Sign-in email</span></a>
            <a href="#settings-security" aria-current={sectionHash === '#settings-security' ? 'location' : undefined}><SecurityMethodIcon method="key" /><span>Sign-in security</span></a>
            <a href="#settings-plan" aria-current={sectionHash === '#settings-plan' ? 'location' : undefined}><CustomerNavigationIcon to="/plan-billing" className="settings-nav-icon" /><span>Plan</span></a>
            <a href="#settings-sessions" aria-current={sectionHash === '#settings-sessions' ? 'location' : undefined}><SecurityMethodIcon method="device" /><span>Sessions</span></a>
            <a href="#settings-data" aria-current={sectionHash === '#settings-data' ? 'location' : undefined}><SecurityMethodIcon method="shield" /><span>Account data</span></a>
          </nav>
          <div className="settings-list">
          <div id="settings-email" className="settings-target" tabIndex={-1}>
          <CustomerEmailChange compact key={JSON.stringify([location.key, session?.email, session?.slug, session?.expiresAt, session?.active])} fallbackEmail={summary?.user.email ?? session?.email ?? 'Unknown'} onSignInAgain={async () => { await request('/auth/logout', { method: 'POST' }); replaceSession(null); navigate('/login', { replace: true }); }} onSignedOut={() => { replaceSession(null); navigate('/login', { replace: true }); }} />
          </div>
          <div id="settings-security" className="settings-target" tabIndex={-1}>
            <SecurityCredentials onSignedOut={() => { replaceSession(null); navigate('/login', { replace: true }); }} />
          </div>

          <section id="settings-plan" tabIndex={-1} className="settings-row settings-row--action settings-target">
            <h2>Plan</h2>
            <div>
              <strong>{formatPlan(plan)}</strong>
              <p>{planSummary(plan)}</p>
            </div>
            <Link to="/plan-billing">Manage plan</Link>
          </section>

          <section id="settings-sessions" tabIndex={-1} className="settings-row settings-row--sessions settings-target">
            <h2 className="settings-method-title"><SecurityMethodIcon method="device" />Session</h2>
            <div className="settings-session-panel">
              <div className="settings-session-heading">
                <div>
                  <strong>Signed-in devices</strong>
                  <p>Review and end any active browser session.</p>
                </div>
                <button type="button" className="settings-signout" onClick={signOut}>Sign out here</button>
              </div>

              {sessionError ? <div className="alert" role="alert">{sessionError}</div> : null}
              {sessionsReadFailed ? (
                <button type="button" className="button-secondary settings-session-retry" onClick={(event) => {
                  event.currentTarget.closest('section')?.focus();
                  setSessionsReadAttempt((attempt) => attempt + 1);
                }}>Retry signed-in devices</button>
              ) : null}
              {sessionsLoading ? <p role="status">Loading signed-in devices...</p> : null}
              {!sessionsLoading && !sessionsReadFailed && browserSessions.length === 0 ? (
                <p>No signed-in devices were returned.</p>
              ) : null}
              {!sessionsLoading && !sessionsReadFailed && browserSessions.length > 0 ? (
                <div className="settings-session-list">
                  {browserSessions.map((browserSession) => (
                    <div className="settings-session-item" key={browserSession.id}>
                      <div>
                        <strong>
                          {browserSession.device}
                          {browserSession.current ? <span className="settings-current-badge">This device</span> : null}
                        </strong>
                        <p>
                          Last active {formatSessionTime(browserSession.lastSeenAt)} · Expires{' '}
                          {formatSessionTime(browserSession.expiresAt)}
                        </p>
                      </div>
                      <button
                        type="button"
                        className="settings-session-revoke"
                        onClick={() => revokeSession(browserSession)}
                        disabled={pendingSessionId === browserSession.id}
                      >
                        {pendingSessionId === browserSession.id
                          ? 'Signing out...'
                          : browserSession.current
                            ? 'Sign out'
                            : 'Sign out device'}
                      </button>
                    </div>
                  ))}
                </div>
              ) : null}

              {browserSessions.length > 1 ? (
                <button
                  type="button"
                  className="settings-signout-all"
                  onClick={revokeAllSessions}
                  disabled={pendingSessionId === 'all'}
                >
                  {pendingSessionId === 'all' ? 'Signing out...' : 'Sign out of all devices'}
                </button>
              ) : null}
            </div>
          </section>

          <section id="settings-data" tabIndex={-1} className="settings-row settings-row--future settings-target">
            <h2>Account data</h2>
            <div>
              <strong>Export and deletion controls are coming later.</strong>
              <p>They will be added with the required backend checks, confirmations, and privacy safeguards.</p>
            </div>
          </section>
          </div>
        </div>
      )}
    </div>
  );
}

export default SettingsPage;
